// react
import { useCallback, useEffect, useState } from 'react';

// msal
import { useMsal } from '@azure/msal-react';

// types
import { InvoicePayload, Invoice } from '../@types';

// services
import {
	createInvoice,
	deactivateInvoice as deactivateInvoiceService,
	fetchInvoice,
	fetchInvoices,
	updateInvoice,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseInvoicesParams {
	contractId?: number;
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseInvoicesReturn {
	create: (payload: InvoicePayload) => Promise<ApiResponse<Invoice>>;
	data: Invoice[];
	deactivate: (id: number) => Promise<ApiResponse<Invoice>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<Invoice[]>['pagination'];
	refetch: () => Promise<void>;
	update: (id: number, payload: Partial<InvoicePayload>) => Promise<ApiResponse<Invoice>>;
}

// Hook

export const useInvoices = ({
	contractId,
	isEnabled = true,
	projectId,
	query = {},
}: UseInvoicesParams = {}): UseInvoicesReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const { accounts, instance } = useMsal();

	const refreshTokens = useCallback(async () => {
		if (accounts.length === 0) return null;
		try {
			const result = await instance.acquireTokenSilent({
				account: accounts[0],
				scopes: ['user.read'],
			});
			if (result.accessToken && result.idToken) {
				sessionStorage.setItem('accessToken', result.accessToken);
				sessionStorage.setItem('idToken', result.idToken);
				return { accessToken: result.accessToken, idToken: result.idToken };
			}
		} catch {
			// fall through to sessionStorage tokens
		}
		return null;
	}, [accounts, instance]);

	// state
	const [data, setData] = useState<Invoice[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<Invoice[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchInvoices(
				{ accessToken, idToken },
				{ ...query, contract: contractId, project: projectId }
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, contractId, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create invoice
	const create = useCallback(
		async (payload: InvoicePayload): Promise<ApiResponse<Invoice>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createInvoice(tokens, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create invoice';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// update invoice
	const update = useCallback(
		async (id: number, payload: Partial<InvoicePayload>): Promise<ApiResponse<Invoice>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateInvoice(tokens, id, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update invoice';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// deactivate invoice
	const deactivateInvoice = useCallback(
		async (id: number): Promise<ApiResponse<Invoice>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateInvoiceService(tokens, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate invoice';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	return {
		create,
		data,
		deactivate: deactivateInvoice,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Invoice Hook

export interface UseInvoiceParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseInvoiceReturn {
	data: Invoice | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<InvoicePayload>) => Promise<ApiResponse<Invoice>>;
}

export const useInvoice = ({
	id,
	isEnabled = true,
	query = {},
}: UseInvoiceParams): UseInvoiceReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const { accounts, instance } = useMsal();

	const refreshTokens = useCallback(async () => {
		if (accounts.length === 0) return null;
		try {
			const result = await instance.acquireTokenSilent({
				account: accounts[0],
				scopes: ['user.read'],
			});
			if (result.accessToken && result.idToken) {
				sessionStorage.setItem('accessToken', result.accessToken);
				sessionStorage.setItem('idToken', result.idToken);
				return { accessToken: result.accessToken, idToken: result.idToken };
			}
		} catch {
			// fall through
		}
		return null;
	}, [accounts, instance]);

	// state
	const [data, setData] = useState<Invoice | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchInvoice({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				// The by-id endpoint returns a one-element array (same shape as the
				// list path, which the v1 app also depends on); unwrap the first row.
				setData(res.data?.[0] ?? null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch invoice');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update invoice
	const update = useCallback(
		async (payload: Partial<InvoicePayload>): Promise<ApiResponse<Invoice>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateInvoice(tokens, id, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update invoice';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, id, fetchData, refreshTokens]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
